import { useCallback, useState } from 'react';

import { notification } from 'antd';
import { useIntl } from 'react-intl';

import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type { WorkJourneyAssignedUser } from '@src/packages/workJourney/types';
import IntlMessages from '@src/util/IntlMessages';

import UsersSelectionDrawer from '../../WorkJourneyCreate/components/UsersSelectionDrawer';

const WorkJourneyAssignedUsersDrawer = () => {
  const intl = useIntl();
  const [saving, setSaving] = useState(false);
  const {
    isVisibleUsersDrawer,
    setIsVisibleUsersDrawer,
    selectedDrawerUsers,
    setSelectedDrawerUsers,
    selectedWorkJourneyId,
    selectedMembersDrawerJourney,
    setSelectedMembersDrawerJourney,
    syncWorkJourneyMembers,
    orgId,
    setRefreshTrigger,
  } = useWorkJourneyContext();

  const requiredMembers = selectedMembersDrawerJourney?.requiredMembers ?? 0;

  const handleClose = useCallback(() => {
    setIsVisibleUsersDrawer(false);
    setSelectedMembersDrawerJourney(null);
  }, [setIsVisibleUsersDrawer, setSelectedMembersDrawerJourney]);

  const handleConfirm = useCallback(
    async (selectedUsers: unknown[]) => {
      const members = selectedUsers as WorkJourneyAssignedUser[];
      setSelectedDrawerUsers(members);

      if (!selectedWorkJourneyId || !orgId) {
        handleClose();
        return;
      }

      setSaving(true);
      try {
        await syncWorkJourneyMembers(selectedWorkJourneyId, members);
        notification.success({
          message: intl.formatMessage({ id: 'workJourney.members.syncSuccess' }),
        });
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        notification.error({
          message: intl.formatMessage({ id: 'workJourney.members.syncError' }),
          description: error instanceof Error ? error.message : String(error),
        });
        return;
      } finally {
        setSaving(false);
      }

      handleClose();
    },
    [
      handleClose,
      intl,
      orgId,
      selectedWorkJourneyId,
      setRefreshTrigger,
      setSelectedDrawerUsers,
      syncWorkJourneyMembers,
    ],
  );

  const journeyDescription = selectedMembersDrawerJourney?.name ?? undefined;

  return (
    <UsersSelectionDrawer
      isVisible={isVisibleUsersDrawer}
      onCancel={handleClose}
      onConfirm={handleConfirm}
      selectedUsers={selectedDrawerUsers}
      loading={saving}
      title={<IntlMessages id="general.users.assigned" />}
      description={journeyDescription}
      requiredMembers={requiredMembers}
    />
  );
};

export default WorkJourneyAssignedUsersDrawer;
